import { useState, type FormEvent } from "react";
import Reveal from "@/components/base/Reveal";

const submitAddr = "https://readdy.ai/api/form/dagmiarep053ijub2ds0";

const topics = ["打榜线索", "直拍对比", "热议八卦", "规则问题", "其他"];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    // honeypot: silently succeed if a bot filled it
    const honeypot = String(data.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "contact_alt") payload.append(key, String(value));
    });

    setSubmitting(true);
    setStatus("idle");
    setFormError("");
    try {
      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.detail || parsed?.meta?.message || parsed?.message || responseText;
      const isSpam = /spam|form data is spam/i.test(String(serverMsg));
      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          isSpam
            ? "内容被系统判定为疑似广告，请调整后重试。"
            : String(serverMsg && serverMsg !== "" ? serverMsg : "提交失败，请稍后再试。")
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="border-b border-background-300/70 bg-background-50 py-12 md:py-16">
      <div className="container-x grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-primary-600">
              <i className="ri-mail-send-line" aria-hidden="true" />
              线索投稿 &amp; 联系我们
            </span>
            <h2 className="mt-3 font-serif text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              你有一手直拍或规则线索？
            </h2>
            <p className="mt-4 text-[15px] leading-7 text-foreground-600">
              无论是打榜规则漏洞、值得对比的舞台、有出处的热议线索，还是对站内内容的纠错建议，
              都欢迎发给我们。核实并确认信源后，我们会标注你的线索来源后整理发布。
            </p>
            <ul className="mt-6 space-y-3 text-sm text-foreground-700">
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                  <i className="ri-mail-line" aria-hidden="true" />
                </span>
                hello@dailycontest.cn
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className="ri-time-line" aria-hidden="true" />
                </span>
                每日 10:00 – 23:00 在线查收线索
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className="ri-shield-check-line" aria-hidden="true" />
                </span>
                线索来源与用途：仅作站内内容整理与信源标注
              </li>
            </ul>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={80}>
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="rounded-lg border border-background-300/70 bg-background-100 p-5 md:p-7"
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-[13px] font-semibold text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    placeholder="如何称呼你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-[13px] font-semibold text-foreground-800">
                    联系邮箱 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于我们回复你"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>
              <div className="mt-4">
                <label htmlFor="contact-topic" className="mb-1.5 block text-[13px] font-semibold text-foreground-800">
                  线索类别
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                >
                  {topics.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mt-4">
                <label htmlFor="contact-message" className="mb-1.5 block text-[13px] font-semibold text-foreground-800">
                  线索内容 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请尽量描述清楚时间、节目与你的发现，方便我们核对信源……"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
                <p className="mt-1 text-right text-xs text-foreground-500">最多 500 字</p>
              </div>

              {/* honeypot (hidden via stylesheet) */}
              <input
                type="text"
                name="contact_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                {status === "success" ? (
                  <p className="flex items-center gap-2 text-sm font-semibold text-primary-700">
                    <i className="ri-checkbox-circle-fill" aria-hidden="true" />
                    收到啦！我们会尽快核对并回复你。
                  </p>
                ) : status === "error" ? (
                  <p className="flex items-start gap-2 text-sm font-semibold text-accent-600">
                    <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
                    <span>{formError}</span>
                  </p>
                ) : (
                  <p className="text-xs text-foreground-500">提交即表示同意我们按线索用途整理你的来信。</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" aria-hidden="true" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill" aria-hidden="true" />
                      提交线索
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}